import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, ArrowRight, Check, Home, Flame, CalendarDays, Search, UserCheck, MessageCircle, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { COMMUNITY_TOUR_VERSION, type CommunityTourState } from '@/lib/communityTourState';
import { communityTourSteps } from './communityTourSteps';

const icons = [Home, Flame, CalendarDays, Search, UserCheck, MessageCircle, User];
const targetFor = (target: string) => document.querySelector<HTMLElement>(`[data-community-tour="${target}"]`);

interface Props {
  pending: boolean;
  step: number;
  onChange: (state: CommunityTourState) => void;
}

export function CommunityWelcomeTour({ pending, step, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [contentHeight, setContentHeight] = useState(280);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const current = communityTourSteps[step] ?? communityTourSteps[0];
  const Icon = icons[step] ?? Home;
  const hasRect = Boolean(rect);

  useLayoutEffect(() => {
    if (!open || !hasRect || !contentRef.current) return;
    const content = contentRef.current;
    const measure = () => setContentHeight(content.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [open, hasRect]);

  useEffect(() => {
    if (!pending) { setOpen(false); setRect(null); return; }
    previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const check = () => {
      if (document.visibilityState !== 'visible') { setOpen(false); return; }
      const targetsReady = communityTourSteps.every(({ target }) => {
        const element = targetFor(target);
        return element && element.getBoundingClientRect().width > 0 && element.getBoundingClientRect().height > 0;
      });
      const otherDialog = document.querySelector('[role="dialog"]:not([data-community-tour-dialog]), [role="alertdialog"]');
      if (targetsReady && !otherDialog) setOpen(true);
      else setOpen(false);
    };
    check();
    const timer = window.setInterval(check, 300);
    return () => window.clearInterval(timer);
  }, [pending]);

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const element = targetFor(current.target);
      if (!element) { setRect(null); return; }
      const bounds = element.getBoundingClientRect();
      setRect(bounds.width > 0 && bounds.height > 0 ? bounds : null);
      if (contentRef.current) setContentHeight(contentRef.current.getBoundingClientRect().height);
    };
    measure();
    const observer = new ResizeObserver(measure);
    const element = targetFor(current.target);
    if (element) observer.observe(element);
    if (contentRef.current) observer.observe(contentRef.current);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [open, current.target]);

  const finish = (status: 'completed' | 'skipped') => {
    setOpen(false);
    onChange({ version: COMMUNITY_TOUR_VERSION, status, step });
  };
  const go = (next: number) => onChange({ version: COMMUNITY_TOUR_VERSION, status: 'pending', step: next });
  const width = Math.min(352, window.innerWidth - 32);
  const left = rect ? Math.max(16, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 16)) : 16;
  const headerTarget = current.target === 'streak' || current.target === 'calendar';
  const availableHeight = rect ? Math.max(120, headerTarget ? window.innerHeight - rect.bottom - 34 : rect.top - 34) : window.innerHeight - 32;
  const top = rect ? (headerTarget ? rect.bottom + 18 : rect.top - contentHeight - 18) : 16;

  return (
    <Dialog.Root open={open && Boolean(rect)} onOpenChange={(next) => { if (!next) finish('skipped'); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="community-tour-overlay" />
        {rect && <div aria-hidden="true" className="community-tour-spotlight" style={{ left: rect.left - 5, top: rect.top - 5, width: rect.width + 10, height: rect.height + 10 }} />}
        <Dialog.Content
          ref={contentRef}
          data-community-tour-dialog
          className="community-tour-content"
          style={{ width, left, top: Math.max(16, top), maxHeight: availableHeight }}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => { event.preventDefault(); if (previousFocus.current?.isConnected) previousFocus.current.focus(); }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"><Icon className="h-5 w-5" aria-hidden="true" /></div>
            <span className="text-xs font-medium text-muted-foreground" aria-live="polite">{step + 1} of {communityTourSteps.length}</span>
          </div>
          <div aria-live="polite" aria-atomic="true" className="space-y-2">
            <Dialog.Title className="text-xl font-semibold text-foreground">{current.title}</Dialog.Title>
            <Dialog.Description className="text-sm leading-relaxed text-muted-foreground">{current.description}</Dialog.Description>
          </div>
          <div className="flex items-center justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={() => finish('skipped')}>Skip tour</Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" aria-label="Back" disabled={step === 0} onClick={() => go(step - 1)}><ArrowLeft className="h-4 w-4" /></Button>
              <Button size="sm" onClick={() => step === communityTourSteps.length - 1 ? finish('completed') : go(step + 1)}>
                {step === communityTourSteps.length - 1 ? 'Done' : 'Next'}
                {step === communityTourSteps.length - 1 ? <Check className="ml-2 h-4 w-4" /> : <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}